import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useStore } from 'zustand/react';
import createTableStore from '@store/table-store/table.store.ts';

export interface UseTableSelectionParams<T> {
  tableStore: ReturnType<typeof createTableStore>;
  // Must be a stable reference: a module-level function or a useCallback.
  getRowKey: (row: T) => string;
  // Rows it rejects can't be selected and are left out of the header state.
  // Must be a stable reference, like getRowKey.
  isRowSelectable?: (row: T) => boolean;
}

export interface UseTableSelectionResult<T> {
  selectedItems: T[];
  selectedKeys: string[];
  isPageAllSelected: boolean;
  isPagePartiallySelected: boolean;
  isRowSelected: (row: T) => boolean;
  toggleRow: (row: T) => void;
  togglePage: () => void;
  setSelectedItems: Dispatch<SetStateAction<T[]>>;
  clearSelection: () => void;
}

/**
 * Row selection for a server-paginated DynamicTable.
 *
 * The table store holds only the current page's rows, so the header checkbox
 * state is scoped to that page while the selection itself accumulates across
 * pages. Subscribing to the store here keeps both in sync when the page
 * changes — reading tableStore.getState() during render would not.
 */
const useTableSelection = <T>({
  tableStore,
  getRowKey,
  isRowSelectable
}: UseTableSelectionParams<T>): UseTableSelectionResult<T> => {
  const tableData = useStore(tableStore, (state) => state.tableData) as T[];
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const selectedKeys = useMemo(
    () => selectedItems.map(getRowKey),
    [selectedItems, getRowKey]
  );

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const selectableRows = useMemo(
    () => (isRowSelectable ? tableData.filter(isRowSelectable) : tableData),
    [tableData, isRowSelectable]
  );

  const pageSelectedCount = useMemo(
    () =>
      selectableRows.filter((row) => selectedKeySet.has(getRowKey(row))).length,
    [selectableRows, selectedKeySet, getRowKey]
  );

  const isPageAllSelected =
    selectableRows.length > 0 && pageSelectedCount === selectableRows.length;

  const isPagePartiallySelected =
    pageSelectedCount > 0 && pageSelectedCount < selectableRows.length;

  const isRowSelected = useCallback(
    (row: T) => selectedKeySet.has(getRowKey(row)),
    [selectedKeySet, getRowKey]
  );

  const toggleRow = useCallback(
    (row: T) => {
      if (isRowSelectable && !isRowSelectable(row)) return;

      const rowKey = getRowKey(row);

      setSelectedItems((prev) =>
        prev.some((item) => getRowKey(item) === rowKey)
          ? prev.filter((item) => getRowKey(item) !== rowKey)
          : [...prev, row]
      );
    },
    [getRowKey, isRowSelectable]
  );

  // Selecting all drops the current page's rows from the selection and adds
  // them back when the page was not fully selected, so the other pages keep
  // their rows and no row can be selected twice.
  const togglePage = useCallback(() => {
    const pageKeys = new Set(selectableRows.map(getRowKey));

    setSelectedItems((prev) => {
      const otherPages = prev.filter((item) => !pageKeys.has(getRowKey(item)));

      return isPageAllSelected
        ? otherPages
        : [...otherPages, ...selectableRows];
    });
  }, [selectableRows, isPageAllSelected, getRowKey]);

  const clearSelection = useCallback(() => setSelectedItems([]), []);

  return {
    selectedItems,
    selectedKeys,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow,
    togglePage,
    setSelectedItems,
    clearSelection
  };
};

export default useTableSelection;
